import { DoneAll, RemoveDone } from "@mui/icons-material";
import { alpha, Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import { ReactNode, useState } from "react";

import { formatLimit } from "@/pages/configuration/pages/chemistry-ranges/hooks/useTableData";
import { buildUploadUrl } from "@/shared/models/Inspection";
import {
	getSupplierColor,
	getSupplierInitial,
} from "@/shared/utils/supplierColors";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
	formatDateOnly,
	formatLength,
} from "../hooks/useSupplierHeatsTableData";
import {
	AppliedRangeVersion,
	ChemistryDraft,
	ElementEvaluation,
	SupplierHeatRow,
	SectionLimit,
} from "../models/ChemistryDraft";
import { getReviewStatusText } from "./AcknowledgeButton";
import { EVALUATION_TONES, EvaluationTone } from "./EvaluationChips";
import { ChemistryResultsTable } from "./ChemistryResultsTable";
import { GradeEditor } from "./GradeEditor";

type Props = {
	heat: SupplierHeatRow | null;
	elements: ElementEvaluation[];
	sectionLimits: SectionLimit[];
	version: AppliedRangeVersion | null;
	canEdit: boolean;
	draft: ChemistryDraft | null;
	isDirty: boolean;
	isSaving: boolean;
	onStartEdit: () => void;
	onDraftChange: (draft: ChemistryDraft) => void;
	onCancel: () => void;
	onSave: () => void;
	isAcknowledging: boolean;
	onToggleAcknowledge: (heat: SupplierHeatRow) => void;
	isSavingGrade: boolean;
	onSaveGrade: (heat: SupplierHeatRow, gradeId: number) => void;
};

const DotChip = ({
	label,
	tone,
	title,
	testId,
}: {
	label: string;
	tone: EvaluationTone;
	title?: string;
	testId?: string;
}) => (
	<Box
		component="span"
		title={title}
		data-test-id={testId}
		sx={() => {
			const { color, bgcolor, dot } = EVALUATION_TONES[tone];

			return {
				display: "inline-flex",
				alignItems: "center",
				gap: 0.75,
				height: 26,
				px: 1.25,
				borderRadius: 999,
				fontSize: 12,
				fontWeight: 500,
				lineHeight: 1,
				whiteSpace: "nowrap",
				color,
				bgcolor,
				"&::before": {
					content: '""',
					width: 7,
					height: 7,
					borderRadius: "50%",
					bgcolor: dot,
					flexShrink: 0,
				},
			};
		}}
	>
		{label}
	</Box>
);

const TruncatedValue = ({
	value,
	href,
	testId,
}: {
	value: ReactNode;
	href?: string | null;
	testId?: string;
}) => {
	const [isTruncated, setIsTruncated] = useState(false);
	const linkProps = href
		? {
				component: "a" as const,
				href,
				target: "_blank",
				rel: "noopener noreferrer",
				color: "primary",
				"data-test-id": testId,
				sx: {
					display: "block",
					textDecoration: "none",
					"&:hover": { textDecoration: "underline" },
				},
			}
		: {};

	return (
		<Tooltip title={isTruncated ? value : ""} arrow placement="top">
			<Typography
				variant="body2"
				fontWeight={600}
				noWrap
				{...linkProps}
				onMouseEnter={(event) =>
					setIsTruncated(
						event.currentTarget.scrollWidth > event.currentTarget.clientWidth,
					)
				}
			>
				{!(value === null || value === undefined || value === "") ? value : "-"}
			</Typography>
		</Tooltip>
	);
};

const Field = ({
	label,
	value,
	minWidth = 0,
	href,
	testId,
	children,
}: {
	label: string;
	value?: ReactNode;
	minWidth?: number;
	href?: string | null;
	testId?: string;
	children?: ReactNode;
}) => (
	<Box sx={{ minWidth }}>
		<Typography
			variant="caption"
			color="text.secondary"
			display="block"
			lineHeight={1.5}
			noWrap
		>
			{label}
		</Typography>
		{children ?? <TruncatedValue value={value} href={href} testId={testId} />}
	</Box>
);

const InfoGroup = ({
	title,
	columns,
	children,
}: {
	title: string;
	columns: string;
	children: ReactNode;
}) => (
	<Box
		sx={{
			minWidth: 0,
			overflow: "hidden",
			px: 2,
			py: 1.5,
			borderRight: "1px solid",
			borderColor: "divider",
			"&:last-of-type": { borderRight: 0 },
		}}
	>
		<Typography
			sx={{
				fontSize: 11,
				fontWeight: 600,
				letterSpacing: "0.08em",
				textTransform: "uppercase",
				color: "text.secondary",
				mb: 1,
			}}
		>
			{title}
		</Typography>
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: columns,
				columnGap: 2,
			}}
		>
			{children}
		</Box>
	</Box>
);

const SectionTitle = ({ children }: { children: string }) => (
	<Typography variant="subtitle2" fontWeight={600} color="text.secondary">
		{children}
	</Typography>
);

const EvaluationSummary = ({ heat }: { heat: SupplierHeatRow }) => {
	if (heat.EvaluationCode === "NO_GRADE")
		return (
			<DotChip
				tone="noGrade"
				label="No grade detected"
				title="The grade printed on the certificate could not be resolved to a plant grade"
			/>
		);
	if (
		heat.EvaluationCode === "NO_SPEC" ||
		heat.EvaluationCode === "NO_CHEMISTRY"
	)
		return (
			<DotChip
				tone={heat.EvaluationCode === "NO_SPEC" ? "noRange" : "neutral"}
				label={heat.EvaluationCode === "NO_SPEC" ? "No spec" : "No chemistry"}
			/>
		);

	const inRangeCount =
		heat.LimitCount - heat.OutOfRangeCount - heat.MissingCount;
	const parts: {
		key: string;
		count: number;
		label: string;
		tone: EvaluationTone;
		title: string;
	}[] = [
		{
			key: "out",
			count: heat.OutOfRangeCount,
			label: "out of range",
			tone: "out",
			title: "Elements outside the grade range",
		},
		{
			key: "missing",
			count: heat.MissingCount,
			label: "missing",
			tone: "missing",
			title: "Elements with a grade range but no reported value",
		},
		{
			key: "in",
			count: inRangeCount,
			label: "in range",
			tone: "in",
			title: "Elements within the grade range",
		},
		{
			key: "without",
			count: heat.WithoutRangeCount,
			label: "without range",
			tone: "noRange",
			title: "Reported elements the grade has no range for",
		},
	];

	return (
		<>
			{parts
				.filter((part) => part.count > 0)
				.map((part) => (
					<DotChip
						key={part.key}
						tone={part.tone}
						label={`${part.count} ${part.label}`}
						title={part.title}
					/>
				))}
		</>
	);
};

export const SupplierHeatDetailsPanel = ({
	heat,
	elements,
	sectionLimits,
	version,
	canEdit,
	draft,
	isDirty,
	isSaving,
	onStartEdit,
	onDraftChange,
	onCancel,
	onSave,
	isAcknowledging,
	onToggleAcknowledge,
	isSavingGrade,
	onSaveGrade,
}: Props) => {
	if (!heat) {
		return (
			<Stack
				alignItems="center"
				justifyContent="center"
				sx={{ height: "100%", p: 3 }}
			>
				<Typography variant="body2" color="text.secondary">
					Select a heat to see its chemistry against the grade requirements
				</Typography>
			</Stack>
		);
	}

	const heatElements = elements.filter(
		(element) => element.ReportHeatId === heat.ReportHeatId,
	);
	const heatSectionLimits = sectionLimits.filter(
		(limit) => limit.ReportHeatId === heat.ReportHeatId,
	);
	const hasRequirements = heat.ChemistryRangesByGradeId != null;
	const isAcknowledged = heat.AcknowledgedAt != null;

	const { status, label, detail } = getReviewStatusText(heat, elements);

	const uploadUrl = buildUploadUrl(heat.FilePath);
	const certificateUrl =
		uploadUrl && heat.PageNumber
			? `${uploadUrl}#page=${heat.PageNumber}`
			: uploadUrl;

	return (
		<Stack
			sx={{
				height: "100%",
				minHeight: 0,
				overflow: "auto",
				containerType: "inline-size",
			}}
		>
			<Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
				<Stack
					direction="row"
					spacing={1.5}
					alignItems="center"
					flexWrap="wrap"
					useFlexGap
				>
					<Tooltip title={heat.Supplier ?? ""} arrow>
						<Box
							sx={{
								width: 40,
								height: 40,
								borderRadius: 1.5,
								flexShrink: 0,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								bgcolor: getSupplierColor(heat.Supplier),
								color: "#fff",
								fontWeight: 700,
								fontSize: 18,
							}}
						>
							{getSupplierInitial(heat.Supplier)}
						</Box>
					</Tooltip>
					<Box sx={{ minWidth: 0, flex: "1 1 200px" }}>
						<Stack
							direction="row"
							spacing={1}
							alignItems="center"
							flexWrap="wrap"
							useFlexGap
						>
							<Typography
								variant="h5"
								fontWeight={700}
								lineHeight={1.2}
								sx={{ fontSize: "1.2rem", mr: 1 }}
							>
								{`Heat ${heat.HeatName ?? "-"}`}
							</Typography>
							<DotChip
								tone={
									status === "REVISION_REQUIRED"
										? "revision"
										: status === "ACKNOWLEDGED"
											? "in"
											: "neutral"
								}
								label={
									status === "ACKNOWLEDGED" ? `${label} · ${detail}` : label
								}
								title={`${label}: ${detail}`}
								testId="heat-review-status"
							/>
						</Stack>
						<Typography variant="body2" color="text.secondary" noWrap>
							{heat.SupplierName ?? heat.Supplier ?? "-"}
						</Typography>
					</Box>
					<Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
						{canEdit && (
							<Button
								variant={isAcknowledged ? "text" : "contained"}
								color="primary"
								disableElevation
								startIcon={isAcknowledged ? <RemoveDone /> : <DoneAll />}
								disabled={isAcknowledging}
								data-test-id="acknowledge-heat-details"
								onClick={() => onToggleAcknowledge(heat)}
								sx={{
									textTransform: "none",
									whiteSpace: "nowrap",
									minWidth: 220,
								}}
							>
								{isAcknowledged
									? "Mark as unacknowledged"
									: "Mark as acknowledged"}
							</Button>
						)}
					</Stack>
				</Stack>
			</Box>

			<Box
				sx={(theme) => ({
					display: "grid",
					gridTemplateColumns:
						"minmax(0, 4fr) minmax(0, 8fr) minmax(0, 5fr) minmax(0, 4fr)",
					borderTop: "1px solid",
					borderBottom: "1px solid",
					borderColor: "divider",
					bgcolor: alpha(theme.palette.text.primary, 0.02),
					"@container (max-width: 1100px)": {
						gridTemplateColumns: "minmax(0, 2fr) minmax(0, 3fr)",
						"& > :nth-of-type(2n)": { borderRight: 0 },
						"& > :nth-of-type(-n + 2)": {
							borderBottom: "1px solid",
							borderColor: "divider",
						},
					},
				})}
			>
				<InfoGroup title="References" columns="repeat(3, minmax(0, 1fr))">
					<Field label="Supplier heat" value={heat.ExternalHeatNumber} />
					<Field label="PO" value={heat.PoNumber} />
					<Field label="Order" value={heat.OrderNumber} />
				</InfoGroup>
				<InfoGroup
					title="Product"
					columns="minmax(0, 1.6fr) minmax(0, 0.8fr) minmax(0, 0.8fr) minmax(0, 2fr) minmax(0, 1fr)"
				>
					<Field label="Product" value={heat.Product} />
					<Field label="Size" value={heat.Size} />
					<Field label="Length" value={formatLength(heat)} />
					<Field label="Spec" value={heat.Spec} />
					<Field label="Condition" value={heat.Condition} />
				</InfoGroup>
				<InfoGroup
					title="Dates"
					columns="minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.4fr)"
				>
					<Field label="Produced" value={formatDateOnly(heat.ProducedDate)} />
					<Field label="Report date" value={formatDateOnly(heat.ReportDate)} />
					<Field
						label="Received"
						value={dxtToLocalServerTime(heat.ReceivedAt, "MM/dd/yy · HH:mm")}
					/>
				</InfoGroup>
				<InfoGroup title="Source" columns="minmax(0, 3fr) minmax(0, 1fr)">
					<Field
						label="Document"
						value={heat.FileName}
						href={certificateUrl}
						testId="open-heat-certificate"
					/>
					<Field label="Page" value={heat.PageNumber} />
				</InfoGroup>
			</Box>

			<Stack spacing={2} sx={{ px: 2.5, py: 2 }}>
				<Stack
					direction="row"
					columnGap={4}
					rowGap={1.5}
					alignItems="center"
					flexWrap="wrap"
					useFlexGap
				>
					<Stack spacing={1} sx={{ width: 340, flexShrink: 0 }}>
						<Typography variant="subtitle1" fontWeight={700}>
							Chemistry Evaluation
						</Typography>
						<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
							<EvaluationSummary heat={heat} />
						</Stack>
					</Stack>
					<Stack
						direction="row"
						columnGap={4}
						rowGap={1.5}
						alignItems="center"
						flexWrap="wrap"
						useFlexGap
						sx={{ ml: 5 }}
					>
						<Field label="Grade" minWidth={130}>
							<GradeEditor
								key={heat.ReportHeatId}
								heat={heat}
								canEdit={canEdit}
								isSaving={isSavingGrade}
								onSave={(gradeId) => onSaveGrade(heat, gradeId)}
							/>
						</Field>
						<Field
							label="Description"
							value={heat.GradeDescription}
							minWidth={200}
						/>
						<Field label="Customer" value={heat.Customer} minWidth={180} />
						<Field label="COP" value={heat.COP} minWidth={60} />
						<Field
							label="Chemistry ranges"
							minWidth={150}
							value={
								version
									? `Rev ${version.Rev ?? "-"} · ${dxtToLocalServerTime(version.EffectiveDate, "MM/dd/yy")}`
									: null
							}
						/>
					</Stack>
				</Stack>

				{heat.GradeId == null ? (
					<Typography variant="body2" color="error.dark" fontWeight={600}>
						The grade printed on the certificate could not be resolved to a
						plant grade.
					</Typography>
				) : (
					!hasRequirements && (
						<Typography variant="body2" color="text.secondary">
							{`Grade ${heat.Grade} has no chemistry ranges in the current version.`}
						</Typography>
					)
				)}

				{heatElements.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						No chemistry loaded for this heat.
					</Typography>
				) : (
					<ChemistryResultsTable
						elements={heatElements}
						hasRequirements={hasRequirements}
						canEdit={canEdit}
						draft={draft?.ReportHeatId === heat.ReportHeatId ? draft : null}
						isDirty={isDirty}
						isSaving={isSaving}
						onStartEdit={onStartEdit}
						onDraftChange={onDraftChange}
						onCancel={onCancel}
						onSave={onSave}
					/>
				)}

				{heatSectionLimits.length > 0 && (
					<Stack spacing={1}>
						<SectionTitle>Section-Specific Ranges</SectionTitle>
						<Stack
							direction="row"
							columnGap={4}
							rowGap={1}
							flexWrap="wrap"
							useFlexGap
						>
							{heatSectionLimits.map((limit) => (
								<Field
									key={`${limit.ElementName}-${limit.SectionName}`}
									label={`${limit.ElementName} (${limit.SectionName})`}
									value={formatLimit(limit)}
								/>
							))}
						</Stack>
					</Stack>
				)}

				{heat.Additions && (
					<Stack spacing={0.5}>
						<SectionTitle>Additions</SectionTitle>
						<Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
							{heat.Additions}
						</Typography>
					</Stack>
				)}
			</Stack>
		</Stack>
	);
};
